import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { Prescription, PrescriptionDataTransformer } from '@/types/prescription';
import { Patient } from '@/types';
import { Doctor } from '@/types/doctor';
import { getPrescriptionById, getPatient, getSettings } from '@/lib/api';
import PrescriptionForm from '@/components/Prescription/PrescriptionForm';
import { displayAge } from '@/lib/patientAge';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString?: Date | string): string => {
  if (!dateString) return 'Unknown date';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return 'Invalid date';
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function EditPrescriptionPage() {
  const { id: prescriptionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Load prescription, then patient + doctor ───────────────────────────────

  useEffect(() => {
    if (!prescriptionId) {
      setError('No prescription ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getPrescriptionById(prescriptionId);
        if (cancelled) return;

        if (!data) {
          setError('Prescription not found');
          return;
        }
        setPrescription(data);

        const patientId = data.patient_id;
        if (patientId) {
          getPatient(patientId)
              .then((p) => { if (!cancelled && p) setPatient(p as Patient); })
              .catch(() => {/* leave null — form still works without it */});
        }

        if (currentUser?.doctor_id && currentUser?.clinic_id) {
          getSettings(currentUser.doctor_id, currentUser.clinic_id)
              .then((s) => {
                if (cancelled || !s) return;
                setDoctor({
                  id:            currentUser.doctor_id ?? '',
                  name:          s.profile?.name       ?? '',
                  specialty:     s.profile?.specialty  ?? 'General Medicine',
                  phone:         s.profile?.phone      ?? '',
                  email:         s.profile?.email      ?? '',
                  clinic_id:     currentUser.clinic_id ?? '',
                  last_modified: '',
                });
              })
              .catch(() => {/* leave null — specialty defaults to General Medicine */});
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading prescription:', err);
          setError('Failed to load prescription');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [prescriptionId, currentUser?.doctor_id, currentUser?.clinic_id]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleClose = () => navigate('/prescriptions');

  const handleSaved = () => {
    toast({
      title: 'Success',
      description: 'Prescription updated successfully',
      variant: 'default',
    });
    navigate('/prescriptions');
  };

  // ── Guard states ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescription...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-destructive" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Error Loading Prescription</h3>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => window.location.reload()} variant="outline">
                  Try Again
                </Button>
                <Button onClick={handleClose}>
                  Go Back
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="prescription-form-container">
        <Card className="medical-card">
          <CardContent className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">Prescription Not Found</h3>
              <p className="text-muted-foreground mb-6">
                The prescription you're looking for doesn't exist or couldn't be loaded.
              </p>
              <Button onClick={handleClose}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const patientId   = prescription.patient_id || '';
  const displayName = patient?.name || prescription.patient_name || 'Patient';
  const patientAgeLabel = displayAge(patient?.age, patient?.date_of_birth);

  return (
    <div className="prescription-form-container">

      {/* Sticky header — mirrors PatientDetailPage's patient header for a consistent edit experience */}
      <div
        className="sticky z-30 bg-card border-b border-border"
        style={{ top: 'var(--topbar-height, 60px)' }}
      >
        <div className="flex items-center justify-between px-4 py-2 min-h-[42px]">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center gap-1 text-xs text-muted-foreground border border-border rounded px-2 py-1 hover:bg-muted/50 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
            <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold flex-shrink-0">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm font-semibold text-foreground">{displayName}</span>
            {patientAgeLabel && (
              <>
                <span className="text-border">·</span>
                <span className="text-xs text-muted-foreground">{patientAgeLabel}</span>
              </>
            )}
            <span className="text-border">·</span>
            <span className="text-xs text-muted-foreground">{formatDate(prescription.date)}</span>
            {doctor?.specialty && (
              <>
                <span className="text-border">·</span>
                <span className="text-xs text-muted-foreground">{doctor.specialty}</span>
              </>
            )}
          </div>

          <span className="text-[11px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20 flex-shrink-0">
            Editing Prescription
          </span>
        </div>
      </div>

      {/* Form — rendered inline (same pattern as the new-prescription flow on PatientDetailPage) */}
      <div className="pt-2">
        <PrescriptionForm
          mode="edit"
          patientId={patientId}
          initialData={PrescriptionDataTransformer.toFormData(prescription, patientId)}
          onSaved={handleSaved}
          onCancel={handleClose}
          showStickyBar={true}
          patient={patient ?? undefined}
          doctor={doctor ?? undefined}
        />
      </div>
    </div>
  );
}
